import { useState, useCallback } from 'react';
import { Check, FlaskConical, Scale, Droplets } from 'lucide-react';
import { useExperiment } from '@/context';
import { AnalyticalBalance } from '@/components/lab';
import ErlenmeyerFlask from '@/components/lab/ErlenmeyerFlask';
import MagneticStirrer from '@/components/lab/MagneticStirrer';
import MortarAndPestle from '@/components/lab/MortarAndPestle';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Standardize NaOH',
    content: 'Pipet 25.00 mL of standard 0.10 M HCl solution into a 250 mL beaker with a 3/4" magnetic stirring bar. Titrate with NaOH using the prescribed increment schedule (5.0 mL, 1.0 mL, 0.5 mL, 0.1 mL). Determine the endpoint and calculate the concentration of NaOH.'
  },
  2: {
    title: 'Weighing 4 Tablets',
    content: 'Weigh 4 aspirin tablets accurately on the analytical balance and record the weight. Determine the average weight per tablet. This mass is crucial for calculating the percentage of ASA.'
  },
  3: {
    title: 'Crush and Dissolve in Ethanol',
    content: 'Crush 4 aspirin tablets and dissolve in about 40 mL of 95% ethanol (CH₃CH₂OH) in a 100 mL volumetric flask. Dilute to the mark with deionised water. Mix thoroughly.',
    chemicalEquation: 'C9H8O4(s) → C9H8O4(in ethanol/water)'
  },
  4: {
    title: 'Prepare Titration Solution',
    content: 'Pipet 50 mL of the aspirin solution into a 250 mL beaker and add 100 mL of water. Immerse the electrode and place a stirring bar on the bottom of the beaker. Ensure the electrode is clear of the stirring bar.'
  },
  6: {
    title: 'Electrode Placement',
    content: 'The pH electrode should be immersed in the solution but not touching the stir bar. This ensures accurate readings during titration.'
  }
};

export default function Part2SamplePrep() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();

  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const partState = state?.experimentData?.samplePreparation || {
    balance: {
      reading: 0,
      isTared: false,
      isStable: true
    },
    sample: {
      tabletMass: null,
      crushed: false,
      dissolved: false
    },
    flask: {
      solutionVolume: 0,
      stirring: false,
      electrodeInserted: false,
      fillLevel: 0
    }
  };

  const [weighing, setWeighing] = useState(false);
  const [grinding, setGrinding] = useState(false);
  const [dissolving, setDissolving] = useState(false);
  const [stirrerOn, setStirrerOn] = useState(false);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('samplePreparation', { ...partState, ...updates });
  }, [partState, updatePartData]);

  const handleTareBalance = useCallback(() => {
    updateState({
      balance: { ...partState.balance, isTared: true, reading: 0 }
    });
  }, [partState.balance, updateState]);

  const handleWeighTablet = useCallback(() => {
    if (!partState.balance.isTared) {
      handleTareBalance();
      return;
    }

    setWeighing(true);
    setTimeout(() => {
      // 4 aspirin tablets, each typically 300-350 mg
      const mass = 1.2 + Math.random() * 0.2;
      updateState({
        balance: { ...partState.balance, reading: mass },
        sample: { ...partState.sample, tabletMass: parseFloat(mass.toFixed(4)) }
      });
      setWeighing(false);
      completeStep('samplePreparation-1');
    }, 2000);
  }, [partState.balance, partState.sample, updateState, completeStep, handleTareBalance]);

  const handleCrushTablet = useCallback(() => {
    if (!partState.sample.tabletMass) return;

    setGrinding(true);
    setTimeout(() => {
      updateState({
        sample: { ...partState.sample, crushed: true }
      });
      setGrinding(false);
      completeStep('samplePreparation-2');
    }, 3000);
  }, [partState.sample, updateState, completeStep]);

  const handleTransferToFlask = useCallback(() => {
    if (!partState.sample.crushed) return;

    updateState({
      flask: { ...partState.flask, fillLevel: 10 }
    });
    completeStep('samplePreparation-3');
  }, [partState.sample.crushed, partState.flask, updateState, completeStep]);

  const handleAddEthanol = useCallback(() => {
    if (!isStepCompleted('samplePreparation-3')) return;

    updateState({
      flask: { ...partState.flask, solutionVolume: 100, fillLevel: 40 }
    });
    completeStep('samplePreparation-4');
  }, [partState.flask, isStepCompleted, updateState, completeStep]);

  const handleDissolveSample = useCallback(() => {
    if (!isStepCompleted('samplePreparation-4')) return;

    setDissolving(true);
    setStirrerOn(true);
    setTimeout(() => {
      updateState({
        sample: { ...partState.sample, dissolved: true },
        flask: { ...partState.flask, stirring: true }
      });
      setDissolving(false);
      completeStep('samplePreparation-5');
    }, 4000);
  }, [partState.sample, partState.flask, isStepCompleted, updateState, completeStep]);

  const handleInsertElectrode = useCallback(() => {
    if (!partState.sample.dissolved) return;

    updateState({
      flask: { ...partState.flask, electrodeInserted: true }
    });
    completeStep('samplePreparation-6');
  }, [partState.sample.dissolved, partState.flask, updateState, completeStep]);

  const getFlaskColor = () => {
    if (partState.sample.dissolved) return '#FFFDE7'; // Clear pale yellow (aspirin in solution)
    if (partState.flask.fillLevel > 10) return '#F5F5DC'; // Murky with undissolved powder
    return '#FFFFFF';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Sample Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Standardize NaOH', hint: 'Titrate 25.00 mL of 0.10 M HCl with NaOH' },
            { id: 2, title: 'Weigh 4 Aspirin Tablets', hint: 'Record weight, determine average weight/tablet' },
            { id: 3, title: 'Crush and Dissolve in Ethanol', hint: 'Dissolve in ~40 mL 95% ethanol in 100 mL volumetric flask' },
            { id: 4, title: 'Prepare Titration Solution', hint: 'Pipet 50 mL aspirin solution into 250 mL beaker, add 100 mL water' },
            { id: 5, title: 'Dissolve Sample', hint: 'Stir until dissolved' },
            { id: 6, title: 'Insert pH Electrode', hint: 'Ensure electrode is clear of stir bar' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`samplePreparation-${step.id}`) ? 'completed' : ''}`}
            >
              <div className={`step-number ${isStepCompleted(`samplePreparation-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`samplePreparation-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                <span className="step-hint">{step.hint}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sample Info */}
        <div className="sample-info">
          <h4>Sample Data</h4>
          <div className="info-row">
            <span>Tablet Mass:</span>
            <span>{partState.sample.tabletMass ? `${partState.sample.tabletMass.toFixed(4)} g` : '-'}</span>
          </div>
          <div className="info-row">
            <span>Status:</span>
            <span>
              {partState.sample.dissolved ? 'Dissolved' :
               partState.sample.crushed ? 'Crushed' :
               partState.sample.tabletMass ? 'Weighed' : 'Not prepared'}
            </span>
          </div>
          <div className="info-row">
            <span>Electrode:</span>
            <span>{partState.flask.electrodeInserted ? 'Inserted' : 'Not inserted'}</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Sample Preparation"
        completedCount={state?.completedSteps.filter(s => s.startsWith('samplePreparation-')).length || 0}
        totalSteps={6}
        equipment={['Analytical balance', 'Mortar and pestle', '100 mL volumetric flask', '250 mL beaker', 'Magnetic stirrer', '95% ethanol']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="sample-prep-workspace">
          <div className="equipment-row">
            {/* Analytical Balance */}
            <div className="equipment-station">
              <AnalyticalBalance
                reading={partState.balance.reading}
                unit="g"
                stable={!weighing}
                onClick={() => {
                  if (!isStepCompleted('samplePreparation-1')) {
                    handleWeighTablet();
                  }
                }}
              />
              <span className="equipment-label">Analytical Balance</span>
              {!partState.balance.isTared && (
                <button className="tare-btn" onClick={handleTareBalance}>
                  Tare
                </button>
              )}
            </div>

            {/* Mortar and Pestle */}
            <div className="equipment-station">
              <MortarAndPestle
                hasContent={partState.sample.tabletMass !== null && !partState.sample.crushed}
                grinding={grinding}
                contentColor="#FFFFFF"
                onClick={() => {
                  if (isStepCompleted('samplePreparation-1') && !isStepCompleted('samplePreparation-2')) {
                    handleCrushTablet();
                  }
                }}
              />
              <span className="equipment-label">Mortar & Pestle</span>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-assembly">
              <ErlenmeyerFlask
                size={250}
                fillLevel={partState.flask.fillLevel}
                fillColor={getFlaskColor()}
                hasStirBar={isStepCompleted('samplePreparation-4')}
                stirring={stirrerOn}
                label="Aspirin Sample"
                active={
                  (isStepCompleted('samplePreparation-2') && !isStepCompleted('samplePreparation-3')) ||
                  (isStepCompleted('samplePreparation-3') && !isStepCompleted('samplePreparation-4')) ||
                  (isStepCompleted('samplePreparation-5') && !isStepCompleted('samplePreparation-6'))
                }
                onClick={() => {
                  if (isStepCompleted('samplePreparation-2') && !isStepCompleted('samplePreparation-3')) {
                    handleTransferToFlask();
                  } else if (isStepCompleted('samplePreparation-3') && !isStepCompleted('samplePreparation-4')) {
                    handleAddEthanol();
                  } else if (isStepCompleted('samplePreparation-5') && !isStepCompleted('samplePreparation-6')) {
                    handleInsertElectrode();
                  }
                }}
              />
              <MagneticStirrer
                speed={5}
                isOn={stirrerOn}
                hasFlask={true}
                onClick={() => {
                  if (isStepCompleted('samplePreparation-4') && !isStepCompleted('samplePreparation-5')) {
                    handleDissolveSample();
                  }
                }}
              />
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {weighing && (
              <div className="status-message">
                <Scale size={20} className="spin" />
                Weighing 4 tablets...
              </div>
            )}

            {grinding && (
              <div className="status-message">
                <Scale size={20} className="pulse" />
                Grinding 4 tablets to fine powder...
              </div>
            )}

            {dissolving && (
              <div className="status-message">
                <Droplets size={20} className="pulse" />
                Dissolving sample... Stirring...
              </div>
            )}

            {partState.flask.electrodeInserted && (
              <div className="status-message success">
                <Check size={20} />
                Sample prepared and electrode inserted. Ready for titration!
              </div>
            )}
          </div>

          {/* Aspirin Info Box */}
          <div className="info-box">
            <h4>Aspirin (Acetylsalicylic Acid)</h4>
            <p>Molecular Formula: C<sub>9</sub>H<sub>8</sub>O<sub>4</sub></p>
            <p>Molar Mass: 180.16 g/mol</p>
            <p>pKa: ~3.5 (weak acid)</p>
          </div>
        </div>
      </LabBench>

      <style>{`
        .sample-prep-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          padding: 20px;
          width: 100%;
        }

        .equipment-row {
          display: flex;
          gap: 50px;
          align-items: flex-end;
        }

        .equipment-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .flask-assembly {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .tare-btn {
          padding: 6px 16px;
          background: var(--bg-dark);
          border: 1px solid var(--border);
          border-radius: 4px;
          color: var(--text-primary);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tare-btn:hover {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }

        .status-area {
          min-height: 50px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          background: rgba(255, 165, 0, 0.2);
          color: #FFA500;
        }

        .status-message.success {
          background: rgba(0, 168, 107, 0.2);
          color: var(--secondary);
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        .pulse {
          animation: pulse 1s ease-in-out infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .info-box {
          background: var(--bg-elevated);
          border-radius: 8px;
          padding: 16px 24px;
          text-align: center;
        }

        .info-box h4 {
          font-size: 14px;
          color: var(--text-primary);
          margin: 0 0 8px 0;
        }

        .info-box p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 4px 0;
        }

        .sample-info {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .sample-info h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 4px 0;
          border-bottom: 1px solid var(--border);
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-row span:first-child {
          color: var(--text-secondary);
        }

        .info-row span:last-child {
          color: var(--text-primary);
          font-family: 'Courier New', monospace;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
